import {Duration, LoadState} from 'src/types/enums';
import {useCallback, useEffect, useState} from 'react';
import {debounce} from 'lodash';
import {buildApiError} from 'src/utils/errors';
import {ApiErrorHandler, ApiErrorHandlerType} from 'src/utils/errors/ApiErrorHandler';

export interface LoaderProps<T> {
  loadOptions: () => Promise<T>;
  initialData: T;
  getDataOnFail?: (currentData: T) => T;
  onLoad?: (data: T, setData: (data: T) => void) => void;
  wait?: Duration;
  errorHandlers?: ApiErrorHandlerType[];
}

function getErrorFromHandlers(
  responseError: Error,
  handlers?: ApiErrorHandlerType[]
): ApiErrorHandler | Error {
  if (!handlers) return responseError;
  for (const handler of handlers) {
    const errorInstance = buildApiError(handler, responseError);
    if (errorInstance) {
      return errorInstance;
    }
  }

  return responseError;
}

/**
 * This hook wraps normal loading behavior, providing a load state and the results.
 * Changes to the loadOptions prop will result in a reload of the data
 *
 * NOTE: Because this function uses the object reference of loadOptions you must pass
 * the same loading function or this will result in unending calls to loadOptions
 *
 */
export function useLoader<T>(props: LoaderProps<T>): [T, LoadState, Error | undefined] {
  const [data, setData] = useState<T>(props.initialData);
  const [loadState, setLoadState] = useState<LoadState>(LoadState.NONE);
  const [error, setError] = useState<Error>();
  const wait = props.wait || Duration.NONE;

  const doLoad = useCallback(
    (fnProps: LoaderProps<T>) => {
      setError(undefined);
      setLoadState(LoadState.LOADING);
      fnProps
        .loadOptions()
        .then((newData) => {
          setData(newData);
          fnProps.onLoad && fnProps.onLoad(newData, setData);
          setLoadState(LoadState.LOADED);
        })
        .catch((loadError) => {
          loadError = getErrorFromHandlers(loadError, props.errorHandlers);
          console.error('Failed loading options', loadError?.toString());
          const failedData = fnProps.getDataOnFail
            ? fnProps.getDataOnFail(data)
            : fnProps.initialData;
          setError(loadError);
          setData(failedData);
          setLoadState(LoadState.FAILED);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const startLoad = useCallback(
    wait > 0 ? debounce<(fnProps: LoaderProps<T>) => void>(doLoad, wait) : doLoad,
    [wait]
  );

  useEffect(() => {
    startLoad(props);
    return () => {
      const cancellable = startLoad as {cancel?: () => void};
      cancellable.cancel?.();
    };
  }, [props.loadOptions]);

  return [data, loadState, error];
}
